import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, FileText, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';

/**
 * KnowledgeStoreModal - Memory Gatekeeper, not document manager.
 * 
 * PHILOSOPHY:
 * - The user is deciding memory boundaries, NOT uploading files
 * - Forgetting is as important as remembering
 * - Calm, respectful, minimal, non-technical
 * - No enthusiasm for "adding more data"
 * - Protect the user's boundaries
 * 
 * TONE:
 * "Take your time. Not everything needs to become part of you."
 */
export function KnowledgeStoreModal({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [mode, setMode] = useState('text');
  const [content, setContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [memoryScope, setMemoryScope] = useState(null);
  const [tags, setTags] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReassurance, setShowReassurance] = useState(false);
  
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setContent('');
    setSelectedFile(null);
    setMemoryScope(null);
    setTags('');
    setMode('text');
    setShowReassurance(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('knowledge.fileTooLarge'));
        return;
      }
      setSelectedFile(file);
      setMode('file');
    }
  };

  // Show reassurance if user has content but hasn't chosen scope after 5 seconds
  React.useEffect(() => {
    if ((content.trim() || selectedFile) && !memoryScope) {
      const timer = setTimeout(() => setShowReassurance(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [content, selectedFile, memoryScope]);

  const handleSubmit = async () => {
    if (!memoryScope) {
      toast.error(t('knowledge.scopeRequired'));
      return;
    }

    if (mode === 'text' && !content.trim()) {
      toast.error(t('knowledge.contentRequired'));
      return;
    }

    if (mode === 'file' && !selectedFile) {
      toast.error(t('knowledge.fileRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'text') {
        await apiClient.post('/knowledge/text', {
          content: content.trim(),
          memory_scope: memoryScope,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : null
        });
      } else {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('memory_scope', memoryScope);
        if (tags) formData.append('tags', tags);
        
        await apiClient.post('/knowledge/file', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Brief confirmation - no recap, no restatement
      if (memoryScope === 'ephemeral') {
        toast.success(t('knowledge.usedOnce'));
      } else {
        toast.success(t('knowledge.handled'));
      }
      
      resetForm();
      onOpenChange(false);

    } catch (error) {
      console.error('Failed to save knowledge:', error);
      toast.error(t('knowledge.errorSaving'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Human-centered scope descriptions - no technical language
  const scopeOptions = [
    {
      value: 'core',
      label: t('knowledge.scope.core'),
      description: t('knowledge.scope.coreHuman'),
      color: 'border-amber-300 bg-amber-50/50'
    },
    {
      value: 'contextual',
      label: t('knowledge.scope.contextual'),
      description: t('knowledge.scope.contextualHuman'),
      color: 'border-blue-300 bg-blue-50/50'
    },
    {
      value: 'ephemeral',
      label: t('knowledge.scope.ephemeral'),
      description: t('knowledge.scope.ephemeralHuman'),
      color: 'border-gray-300 bg-gray-50/50'
    }
  ];

  const hasContent = content.trim() || selectedFile;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent 
        className="max-w-lg flex flex-col p-0 gap-0"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header - minimal */}
        <DialogHeader className="px-6 py-5 border-b">
          <DialogTitle className="text-lg font-medium text-gray-700">
            {t('knowledge.title')}
          </DialogTitle>
          <p className="text-sm text-gray-400 mt-1">
            {t('knowledge.subtitle')}
          </p>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Mode selector - subtle */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('text')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm flex items-center justify-center gap-2 transition-all ${
                mode === 'text' 
                  ? 'bg-gray-100 text-gray-700' 
                  : 'text-gray-400 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4" />
              {t('knowledge.pasteText')}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`flex-1 py-2 px-3 rounded-lg text-sm flex items-center justify-center gap-2 transition-all ${
                mode === 'file' && selectedFile
                  ? 'bg-gray-100 text-gray-700' 
                  : 'text-gray-400 hover:bg-gray-50'
              }`}
            >
              <Upload className="w-4 h-4" />
              {t('knowledge.uploadFile')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md,.json,.csv,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Content input */}
          {mode === 'text' ? (
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('knowledge.textPlaceholder')}
              className="min-h-[120px] resize-none border-gray-200 focus:border-gray-300"
              disabled={isSubmitting}
            />
          ) : selectedFile ? (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">{selectedFile.name}</p>
                    <p className="text-xs text-gray-400">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedFile(null); setMode('text'); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="p-6 border border-dashed border-gray-200 rounded-lg text-center cursor-pointer hover:border-gray-300 transition-colors"
            >
              <Upload className="w-6 h-6 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">{t('knowledge.dropFile')}</p>
            </div>
          )}

          {/* Memory scope - MANDATORY, shown after content exists */}
          {hasContent && (
            <div className="space-y-4 animate-in fade-in duration-300">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {t('knowledge.howToRemember')}
                </p>
                <p className="text-xs text-gray-400">
                  {t('knowledge.chooseCarefully')}
                </p>
              </div>
              
              <div className="space-y-2">
                {scopeOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setMemoryScope(option.value);
                      setShowReassurance(false);
                    }}
                    className={`w-full p-4 rounded-lg border-2 text-right transition-all ${
                      memoryScope === option.value 
                        ? `${option.color} border-opacity-100` 
                        : 'bg-white border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-700">{option.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>

              {/* Reassurance message - if user hesitates */}
              {showReassurance && !memoryScope && (
                <p className="text-xs text-gray-400 text-center animate-in fade-in duration-500">
                  {t('knowledge.reassurance')}
                </p>
              )}
            </div>
          )}

          {/* Optional tags - very subtle */}
          {memoryScope && memoryScope !== 'ephemeral' && (
            <div className="space-y-2 animate-in fade-in duration-300">
              <Label className="text-xs text-gray-400">
                {t('knowledge.tags')}
              </Label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder={t('knowledge.tagsPlaceholder')}
                className="text-sm border-gray-200"
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>

        {/* Actions - minimal */}
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => { resetForm(); onOpenChange(false); }}
            disabled={isSubmitting}
            className="text-gray-500"
          >
            {t('common.cancel')}
          </Button>
          {hasContent && memoryScope && (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-gray-800 hover:bg-gray-700"
            >
              {memoryScope === 'ephemeral' 
                ? t('knowledge.useNow')
                : t('knowledge.confirm')
              }
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KnowledgeStoreModal;
